import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  AlertTriangle,
  Package,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Lock,
  Unlock,
  FileDown,
  RefreshCw,
  CheckCircle,
  Clock,
  BarChart2,
  Activity,
  FileText,
  Wrench,
  Zap,
} from 'lucide-react';

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white dark:bg-slate-800 p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon size={16} className="text-white" />
      </div>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{title}</p>
    </div>
    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
    {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

// ── Section Card ─────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon: Icon, iconColor = 'text-blue-500', children }) => (
  <div className="mt-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-4">
    <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3 flex items-center gap-2">
      <Icon size={15} className={iconColor} />
      {title}
    </h3>
    {children}
  </div>
);

// ── Status Pill ───────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  active:    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  expired:   'bg-amber-100  dark:bg-amber-900/30  text-amber-700  dark:text-amber-300',
  suspended: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  pending:   'bg-blue-100   dark:bg-blue-900/30   text-blue-700   dark:text-blue-300',
  churned:   'bg-red-100    dark:bg-red-900/30    text-red-700    dark:text-red-300',
};
const StatusPill = ({ label, count }) => (
  <div className={`flex flex-col items-center px-4 py-2 rounded-lg text-xs font-semibold ${STATUS_COLORS[label] || 'bg-gray-100 text-gray-600'}`}>
    <span className="text-lg font-bold">{count ?? 0}</span>
    <span className="capitalize mt-0.5">{label}</span>
  </div>
);

// ── Breakdown Sections (shared between live + saved detail) ───────────────────
const ActivationsSection = ({ data, fmt }) => {
  if (!data) return null;
  const { activations_by_package, activations_by_type, activations_total_amount, total_activations } = data;
  if (!total_activations && !activations_total_amount) return null;
  return (
    <SectionCard title="Activations" icon={Zap} iconColor="text-yellow-500">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{total_activations ?? 0}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
          <p className="font-bold text-gray-900 dark:text-gray-100">{fmt(activations_total_amount)}</p>
        </div>
        {['regular', 'new', 'shift'].map(type => {
          const t = activations_by_type?.[type];
          return t ? (
            <div key={type} className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{type === 'new' ? 'New Install' : type === 'shift' ? 'Shifted' : 'Regular'}</p>
              <p className="font-bold text-gray-900 dark:text-gray-100">{t.count}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{fmt(t.total_amount)}</p>
            </div>
          ) : null;
        })}
      </div>
      {activations_by_package?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
          {activations_by_package.map((p, i) => (
            <div key={i} className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{p.service}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-0.5">{p.count}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{fmt(p.total_amount)}</p>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const CustomerStatusSection = ({ snapshot }) => {
  if (!snapshot || !snapshot.total) return null;
  const statuses = ['active', 'expired', 'suspended', 'pending', 'churned'];
  return (
    <SectionCard title="Customer Status Snapshot" icon={Users} iconColor="text-purple-500">
      <div className="flex flex-wrap gap-2">
        {statuses.map(s => snapshot[s] != null && snapshot[s] > 0 ? (
          <StatusPill key={s} label={s} count={snapshot[s]} />
        ) : null)}
        <div className="flex flex-col items-center px-4 py-2 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
          <span className="text-lg font-bold">{snapshot.total ?? 0}</span>
          <span className="mt-0.5">Total</span>
        </div>
      </div>
    </SectionCard>
  );
};

const ComplaintsSection = ({ stats }) => {
  if (!stats) return null;
  const { filed, resolved, open_total, by_status } = stats;
  const statusColors = { open: 'text-red-600', in_progress: 'text-amber-600', resolved: 'text-emerald-600', closed: 'text-gray-500' };
  return (
    <SectionCard title="Complaints" icon={Activity} iconColor="text-red-500">
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5 text-center">
          <p className="text-xs text-blue-600 dark:text-blue-400">Filed</p>
          <p className="font-bold text-lg text-blue-700 dark:text-blue-300">{filed ?? 0}</p>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2.5 text-center">
          <p className="text-xs text-emerald-600 dark:text-emerald-400">Resolved</p>
          <p className="font-bold text-lg text-emerald-700 dark:text-emerald-300">{resolved ?? 0}</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2.5 text-center">
          <p className="text-xs text-red-600 dark:text-red-400">Open Total</p>
          <p className="font-bold text-lg text-red-700 dark:text-red-300">{open_total ?? 0}</p>
        </div>
      </div>
      {by_status && Object.keys(by_status).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1">
          {Object.entries(by_status).map(([s, count]) => (
            <span key={s} className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-700 ${statusColors[s] || 'text-gray-600'}`}>
              {s.replace('_', ' ')}: {count}
            </span>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const InvoicesSection = ({ stats, fmt }) => {
  if (!stats || !stats.generated) return null;
  const { generated, paid_count, total_billed, total_paid, outstanding } = stats;
  const paidPct = total_billed > 0 ? Math.round(total_paid / total_billed * 100) : 0;
  return (
    <SectionCard title="Monthly Invoices" icon={FileText} iconColor="text-blue-500">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Generated</p>
          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{generated}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Paid</p>
          <p className="font-bold text-lg text-emerald-600 dark:text-emerald-400">{paid_count} <span className="text-xs font-normal">({paidPct}%)</span></p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Billed</p>
          <p className="font-bold text-gray-900 dark:text-gray-100">{fmt(total_billed)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Outstanding</p>
          <p className={`font-bold ${outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmt(outstanding)}</p>
        </div>
      </div>
    </SectionCard>
  );
};

// ── Summary Box ──────────────────────────────────────────────────────────────
const SummaryBox = ({ data, fmt }) => {
  if (!data) return null;
  const collection = Number(data.total_collection || 0);
  const expenses   = Number(data.total_expenses   || 0);
  // Sum per-officer outstanding (assigned − received per officer, capped at 0) — matches Daily Recovery
  const pending    = (data.officer_pending_breakdown || []).reduce((s, o) => s + (o.outstanding || 0), 0);
  const net        = collection - expenses - pending;
  return (
    <div className="mb-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-600 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Monthly Summary</h3>
      </div>
      <div className="p-4 space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-gray-600 dark:text-gray-400">Total Collection</span>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmt(collection)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 dark:text-gray-400">− Total Expenses</span>
          <span className="font-semibold text-red-600 dark:text-red-400">− {fmt(expenses)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 dark:text-gray-400">− New Pending (This Month)</span>
          <span className="font-semibold text-amber-600 dark:text-amber-400">− {fmt(pending)}</span>
        </div>
        <div className="border-t border-gray-200 dark:border-slate-600 pt-2 flex justify-between items-center">
          <span className="font-bold text-gray-800 dark:text-gray-100">= Net Balance</span>
          <span className={`text-base font-bold ${net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {net < 0 ? '− ' : ''}{fmt(Math.abs(net))}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Collapsible Missed Renewals ───────────────────────────────────────────────
const MissedRenewalsSection = ({ missed_renewals, missed_customers, size = 'md' }) => {
  const [open, setOpen] = React.useState(false);
  if (!missed_renewals) return null;
  const isSmall = size === 'sm';
  return (
    <div className={`${isSmall ? '' : 'mt-4'} bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors rounded-xl"
      >
        <span className={`font-semibold text-gray-800 dark:text-gray-100 ${isSmall ? 'text-xs uppercase' : 'text-sm'} flex items-center gap-2`}>
          <AlertTriangle size={isSmall ? 14 : 16} className="text-orange-500" />
          Missed Renewals
          <span className="ml-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-bold">
            {missed_renewals}
          </span>
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && missed_customers?.length > 0 && (
        <div className="border-t border-gray-100 dark:border-slate-700 overflow-x-auto">
          <table className={`w-full text-left ${isSmall ? 'text-xs' : 'text-sm'}`}>
            <thead className="text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-100 dark:border-slate-700">
              <tr>
                <th className="py-2 px-4">Customer</th>
                <th className="py-2 px-4">Username</th>
                <th className="py-2 px-4">Package</th>
                <th className="py-2 px-4">Area</th>
                <th className="py-2 px-4">Expiry</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
              {missed_customers.map((c) => (
                <tr key={c.id}>
                  <td className="py-2 px-4 text-gray-900 dark:text-gray-100">{c.name}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{c.username}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{c.package}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{c.area}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{c.expiry_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const InstallationsSection = ({ stats, fmt }) => {
  if (!stats || !stats.created) return null;
  const { created, paid, pending, partial, total_value } = stats;
  return (
    <SectionCard title="New Installations" icon={Wrench} iconColor="text-indigo-500">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{created}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Paid</p>
          <p className="font-bold text-lg text-emerald-600 dark:text-emerald-400">{paid}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Partial</p>
          <p className="font-bold text-lg text-amber-600 dark:text-amber-400">{partial}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
          <p className="font-bold text-lg text-red-600 dark:text-red-400">{pending}</p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-2.5 text-center sm:col-span-1 col-span-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Value</p>
          <p className="font-bold text-gray-900 dark:text-gray-100">{fmt(total_value)}</p>
        </div>
      </div>
    </SectionCard>
  );
};

// ── Monthly Closing Component ────────────────────────────────────────────────
const MonthlyClosing = () => {
  const [closings, setClosings] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState({});
  const [computing, setComputing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear());
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchClosings = useCallback(async () => {
    try {
      const res = await api.get('/monthly-closings/');
      setClosings(res.data.results || res.data);
    } catch (err) {
      console.error('Failed to fetch closings:', err);
    }
  }, []);

  const prevMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const fetchLiveData = useCallback(async () => {
    try {
      const res = await api.get('/monthly-closings/live/', { params: { month: selectedMonth } });
      setLiveData(res.data);
    } catch (err) {
      console.error('Failed to fetch live data:', err);
    }
  }, [selectedMonth]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchClosings(), fetchLiveData()]);
      setLoading(false);
    };
    init();
  }, [fetchClosings, fetchLiveData]);

  const handleCompute = async () => {
    setComputing(true);
    try {
      await api.post('/monthly-closings/compute/', { month: selectedMonth });
      await Promise.all([fetchClosings(), fetchLiveData()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to compute closing.');
    } finally {
      setComputing(false);
    }
  };

  const handleLock = async (id) => {
    if (!confirm('Lock this month? Numbers will be frozen and cannot be recomputed.')) return;
    try {
      await api.post(`/monthly-closings/${id}/lock/`);
      await fetchClosings();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to lock.');
    }
  };

  const handleUnlock = async (id) => {
    try {
      await api.post(`/monthly-closings/${id}/unlock/`);
      await fetchClosings();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to unlock.');
    }
  };

  const handleDownloadPdf = async (id, month) => {
    setDownloadingId(id);
    try {
      const res = await api.get(`/monthly-closings/${id}/pdf/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `closing_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!expandedData[id]) {
      try {
        const res = await api.get(`/monthly-closings/${id}/`);
        setExpandedData(prev => ({ ...prev, [id]: res.data }));
      } catch (err) {
        console.error('Failed to fetch closing detail:', err);
      }
    }
  };

  const fmt = (n) => `PKR ${Number(n || 0).toLocaleString()}`;
  const monthLabel = (m) => {
    if (!m) return '';
    const [year, month] = m.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Check if selected month already has a closing
  const existingClosing = closings.find(c => c.month === selectedMonth + '-01' || c.month === selectedMonth);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Monthly Closing</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Compute and lock month-end summaries for your records</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-1 py-1">
            <button onClick={prevMonth} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 px-2 min-w-[120px] text-center">
              {monthLabel(selectedMonth)}
            </span>
            <button
              onClick={nextMonth}
              disabled={selectedMonth >= currentMonthStr}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            onClick={handleCompute}
            disabled={computing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={computing ? 'animate-spin' : ''} />
            {existingClosing ? 'Recompute' : 'Close Month'}
          </button>
        </div>
      </div>

      {/* Live Preview for Selected Month */}
      {liveData && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-blue-500" />
            {monthLabel(selectedMonth)}
            {liveData.locked && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                <Lock size={10} /> Locked
              </span>
            )}
            {liveData.is_live && !liveData.closing_id && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                <Clock size={10} /> Live Preview
              </span>
            )}
          </h2>

          <SummaryBox data={liveData} fmt={fmt} />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <StatCard title="Total Collection" value={fmt(liveData.total_collection)} icon={DollarSign} color="bg-emerald-500" />
            <StatCard title="Total Expenses" value={fmt(liveData.total_expenses)} icon={TrendingDown} color="bg-red-500" />
            <StatCard title="Total Outstanding" value={fmt(liveData.total_outstanding ?? liveData.closing_pending)} icon={AlertTriangle} color="bg-orange-500" subtitle="All months combined" />
            <StatCard title="Expected Revenue" value={fmt(liveData.expected_revenue)} icon={DollarSign} color="bg-blue-500" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <StatCard title="Activations" value={liveData.total_activations} icon={Package} color="bg-indigo-500" />
            <StatCard title="New Customers" value={liveData.new_customers} icon={Users} color="bg-purple-500" />
            <StatCard title="Prev. Months Due" value={fmt(liveData.opening_pending)} icon={Clock} color="bg-amber-600" subtitle="Inherited outstanding" />
            <StatCard title="New Pending (Month)" value={fmt(liveData.monthly_pending)} icon={Clock} color="bg-amber-500" subtitle="Generated this month" />
          </div>

          {/* Pending Recovery Breakdown */}
          <div className="mb-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-amber-100 dark:border-amber-900/30 p-4">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3 flex items-center gap-2">
              <AlertTriangle size={15} className="text-amber-500" />
              Pending Recovery Summary
            </h3>
            <div className="grid grid-cols-3 gap-3 text-sm mb-3">
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5">
                <p className="text-xs text-amber-700 dark:text-amber-400">Previous Months Due</p>
                <p className="font-bold text-amber-900 dark:text-amber-200">{fmt(liveData.opening_pending)}</p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2.5">
                <p className="text-xs text-orange-700 dark:text-orange-400">New This Month</p>
                <p className="font-bold text-orange-900 dark:text-orange-200">
                  {fmt((liveData.officer_pending_breakdown || []).reduce((s, o) => s + (o.outstanding || 0), 0))}
                </p>
                <p className="text-xs text-orange-500 dark:text-orange-400 mt-0.5">After received payments</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2.5 border border-red-200 dark:border-red-800">
                <p className="text-xs text-red-700 dark:text-red-400">Total Outstanding</p>
                <p className="font-bold text-red-900 dark:text-red-200">{fmt(liveData.total_outstanding ?? liveData.closing_pending)}</p>
                <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">After all received payments</p>
              </div>
            </div>
            {liveData.prev_months_breakdown?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 select-none">
                  Previous months detail ({liveData.prev_months_breakdown.length} entries)
                </summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {liveData.prev_months_breakdown.filter(b => b.outstanding > 0).map((b, i) => (
                    <div key={i} className="flex justify-between text-gray-600 dark:text-gray-400">
                      <span>{b.label}</span>
                      <span className="font-medium text-orange-700 dark:text-orange-400">{fmt(b.outstanding)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>

          {/* Live data breakdowns */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Collection by method */}
            {liveData.collection_by_method && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-4">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Collection by Method</h3>
                <div className="space-y-2">
                  {Object.entries(liveData.collection_by_method).map(([method, amount]) => (
                    <div key={method} className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400 capitalize">{method}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expense breakdown */}
            {liveData.expense_breakdown && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-4">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Expense Breakdown</h3>
                <div className="space-y-2">
                  {Object.entries(liveData.expense_breakdown).map(([key, amount]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400 capitalize">{key.replace('_', ' ')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Missed renewals — collapsible */}
          {liveData.missed_renewals > 0 && (
            <MissedRenewalsSection
              missed_renewals={liveData.missed_renewals}
              missed_customers={liveData.missed_customers}
            />
          )}

          {/* Collection by officer */}
          {liveData.collection_by_officer?.length > 0 && (
            <div className="mt-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-4">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Officer Performance</h3>
              <div className="space-y-2">
                {liveData.collection_by_officer.map((o) => (
                  <div key={o.officer_id} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{o.officer_name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 dark:text-gray-500">{o.count} entries</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(o.collected)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Officer pending breakdown */}
          <ActivationsSection data={liveData} fmt={fmt} />
          <CustomerStatusSection snapshot={liveData.customer_status_snapshot} />
          <ComplaintsSection stats={liveData.complaints_stats} />
          <InstallationsSection stats={liveData.new_installations_stats} fmt={fmt} />

          {liveData.officer_pending_breakdown?.length > 0 && (
            <div className="mt-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-4">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3 flex items-center gap-2">
                <Clock size={15} className="text-amber-500" />
                Officer Pending — This Month
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-100 dark:border-slate-700">
                      <th className="pb-2 text-left font-medium">Officer</th>
                      <th className="pb-2 text-right font-medium">Assigned</th>
                      <th className="pb-2 text-right font-medium">Received</th>
                      <th className="pb-2 text-right font-medium">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                    {liveData.officer_pending_breakdown.map((o) => (
                      <tr key={o.officer_id}>
                        <td className="py-2 text-gray-800 dark:text-gray-200">{o.officer_name}</td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">{fmt(o.assigned)}</td>
                        <td className="py-2 text-right text-emerald-600 dark:text-emerald-400">{fmt(o.received)}</td>
                        <td className={`py-2 text-right font-semibold ${o.outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {fmt(o.outstanding)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 dark:border-slate-600">
                    <tr className="text-sm font-bold">
                      <td className="pt-2 text-gray-700 dark:text-gray-300">Total</td>
                      <td className="pt-2 text-right text-gray-700 dark:text-gray-300">
                        {fmt(liveData.officer_pending_breakdown.reduce((s, o) => s + o.assigned, 0))}
                      </td>
                      <td className="pt-2 text-right text-emerald-600 dark:text-emerald-400">
                        {fmt(liveData.officer_pending_breakdown.reduce((s, o) => s + o.received, 0))}
                      </td>
                      <td className="pt-2 text-right text-red-600 dark:text-red-400">
                        {fmt(liveData.officer_pending_breakdown.reduce((s, o) => s + o.outstanding, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Past Closings */}
      {closings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <CheckCircle size={18} className="text-green-500" />
              Closed Months
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilterYear(y => y - 1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 w-12 text-center">{filterYear}</span>
              <button
                onClick={() => setFilterYear(y => y + 1)}
                disabled={filterYear >= new Date().getFullYear()}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {closings.filter(c => new Date(c.month).getFullYear() === filterYear).map((closing) => {
              const isExpanded = expandedId === closing.id;
              const detail = expandedData[closing.id];

              return (
                <div key={closing.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
                  {/* Summary Row (always visible) */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    onClick={() => toggleExpand(closing.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          size={18}
                          className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                        <span className="font-bold text-gray-900 dark:text-gray-100">{monthLabel(closing.month)}</span>
                      </div>
                      {closing.locked && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                          <Lock size={10} /> Locked
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-6 text-sm">
                      <div className="hidden sm:flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Collected</p>
                          <p className="font-semibold text-emerald-700 dark:text-emerald-400">{fmt(closing.total_collection)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Expenses</p>
                          <p className="font-semibold text-red-700 dark:text-red-400">{fmt(closing.total_expenses)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Net</p>
                          <p className={`font-bold ${closing.net_balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                            {fmt(closing.net_balance)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Outstanding</p>
                          <p className="font-semibold text-amber-700 dark:text-amber-400">{fmt(closing.closing_pending)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Missed</p>
                          <p className="font-semibold text-orange-700 dark:text-orange-400">{closing.missed_renewals}</p>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {!closing.locked ? (
                          <button
                            onClick={() => handleLock(closing.id)}
                            className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                            title="Lock"
                          >
                            <Lock size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnlock(closing.id)}
                            className="p-2 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                            title="Unlock (admin only)"
                          >
                            <Unlock size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownloadPdf(closing.id, closing.month)}
                          disabled={downloadingId === closing.id}
                          className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
                          title="Download PDF"
                        >
                          {downloadingId === closing.id
                            ? <RefreshCw size={16} className="animate-spin" />
                            : <FileDown size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Mobile summary (visible on small screens only) */}
                  <div className="sm:hidden px-4 pb-3 -mt-1" onClick={() => toggleExpand(closing.id)}>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Collected: </span>
                        <span className="font-medium text-emerald-700 dark:text-emerald-400">{fmt(closing.total_collection)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Expenses: </span>
                        <span className="font-medium text-red-700 dark:text-red-400">{fmt(closing.total_expenses)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Net: </span>
                        <span className={`font-bold ${closing.net_balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                          {fmt(closing.net_balance)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && detail && (
                    <div className="border-t border-gray-100 dark:border-slate-700 p-4">
                      {/* Summary stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Expected Revenue</p>
                          <p className="font-bold text-gray-900 dark:text-gray-100">{fmt(detail.expected_revenue)}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Collection Rate</p>
                          <p className="font-bold text-gray-900 dark:text-gray-100">
                            {detail.expected_revenue > 0 ? Math.round(detail.total_collection / detail.expected_revenue * 100) : 0}%
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Activations</p>
                          <p className="font-bold text-gray-900 dark:text-gray-100">{detail.total_activations}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">New Customers</p>
                          <p className="font-bold text-gray-900 dark:text-gray-100">{detail.new_customers}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        {/* Collection by method */}
                        {detail.collection_by_method && (
                          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                            <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-xs uppercase mb-2">Collection by Method</h4>
                            <div className="space-y-1.5">
                              {Object.entries(detail.collection_by_method).map(([method, amount]) => (
                                <div key={method} className="flex justify-between text-sm">
                                  <span className="text-gray-600 dark:text-gray-400 capitalize">{method}</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Expense breakdown */}
                        {detail.expense_breakdown && (
                          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                            <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-xs uppercase mb-2">Expense Breakdown</h4>
                            <div className="space-y-1.5">
                              {Object.entries(detail.expense_breakdown).map(([key, amount]) => (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-gray-600 dark:text-gray-400 capitalize">{key.replace('_', ' ')}</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Officer performance */}
                      {detail.collection_by_officer?.length > 0 && (
                        <div className="mb-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-xs uppercase mb-2">Officer Performance</h4>
                          <div className="space-y-1.5">
                            {detail.collection_by_officer.map((o) => (
                              <div key={o.officer_id} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600 dark:text-gray-400">{o.officer_name}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-gray-500 dark:text-gray-500">{o.count} entries</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(o.collected)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Officer pending breakdown */}
                      {detail.officer_pending_breakdown?.length > 0 && (
                        <div className="mb-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-xs uppercase mb-2 flex items-center gap-1.5">
                            <Clock size={12} className="text-amber-500" />
                            Officer Pending — This Month
                          </h4>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-slate-600">
                                <th className="pb-1.5 text-left font-medium">Officer</th>
                                <th className="pb-1.5 text-right font-medium">Assigned</th>
                                <th className="pb-1.5 text-right font-medium">Received</th>
                                <th className="pb-1.5 text-right font-medium">Outstanding</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-600">
                              {detail.officer_pending_breakdown.map((o) => (
                                <tr key={o.officer_id}>
                                  <td className="py-1.5 text-gray-800 dark:text-gray-200">{o.officer_name}</td>
                                  <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">{fmt(o.assigned)}</td>
                                  <td className="py-1.5 text-right text-emerald-600 dark:text-emerald-400">{fmt(o.received)}</td>
                                  <td className={`py-1.5 text-right font-semibold ${o.outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                    {fmt(o.outstanding)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="border-t-2 border-gray-200 dark:border-slate-600">
                              <tr className="font-bold">
                                <td className="pt-1.5 text-gray-700 dark:text-gray-300">Total</td>
                                <td className="pt-1.5 text-right text-gray-700 dark:text-gray-300">
                                  {fmt(detail.officer_pending_breakdown.reduce((s, o) => s + o.assigned, 0))}
                                </td>
                                <td className="pt-1.5 text-right text-emerald-600 dark:text-emerald-400">
                                  {fmt(detail.officer_pending_breakdown.reduce((s, o) => s + o.received, 0))}
                                </td>
                                <td className="pt-1.5 text-right text-red-600 dark:text-red-400">
                                  {fmt(detail.officer_pending_breakdown.reduce((s, o) => s + o.outstanding, 0))}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}

                      {/* Summary box + extended stats */}
                      <SummaryBox data={detail} fmt={fmt} />
                      <ActivationsSection data={detail} fmt={fmt} />
                      <CustomerStatusSection snapshot={detail.customer_status_snapshot} />
                      <ComplaintsSection stats={detail.complaints_stats} />
                      <InstallationsSection stats={detail.new_installations_stats} fmt={fmt} />

                      {/* Missed renewals — collapsible */}
                      {detail.missed_renewals > 0 && (
                        <div className="mb-4">
                          <MissedRenewalsSection
                            missed_renewals={detail.missed_renewals}
                            missed_customers={detail.missed_customers}
                            size="sm"
                          />
                        </div>
                      )}

                      {/* Notes */}
                      {detail.notes && (
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                          <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-xs uppercase mb-1">Notes</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{detail.notes}</p>
                        </div>
                      )}

                      {/* Locked info */}
                      {detail.locked && detail.locked_by_name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                          Locked by {detail.locked_by_name} on {new Date(detail.locked_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  {isExpanded && !detail && (
                    <div className="border-t border-gray-100 dark:border-slate-700 p-8 flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {closings.length === 0 && !liveData && (
        <div className="text-center py-16">
          <Calendar size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No monthly closings yet.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Select a month and click "Close Month" to create your first closing.</p>
        </div>
      )}
    </div>
  );
};

export default MonthlyClosing;
